/**
 * SMOKEY'S - DELIVERY DYNAMIC UI
 * ================================
 * Scans [data-dyn] spans and updates textContent with live delivery data.
 * Zone-locked elements use hub→zone centroid (stable "menu pricing").
 * Non-zone-locked elements use user→hub (personalized pricing).
 *
 * Usage:
 *   <span data-dyn="expressFee" data-zone="sherwood-park" data-zone-lock>$4.99</span>
 *   <span data-dyn="standardEta" data-zone="downtown" data-zone-lock>45-60 min</span>
 *   <span data-dyn="minOrder">$19.99</span>
 *
 * Wrapper visibility:
 *   <div data-delivery-only>...</div>   (hidden when market.pickupOnly)
 *   <div data-pickup-only>...</div>     (hidden when NOT pickupOnly)
 *
 * Requires: delivery-config.js (window.SMOKEYS_DELIVERY) loaded first.
 */
(function() {
  'use strict';

  // Singleton guard
  if (window.__SMOKEYS_DELIVERY_UI__) return;
  window.__SMOKEYS_DELIVERY_UI__ = true;

  // Only run on delivery pages
  if (!document.querySelector('[data-delivery-page]') && !document.querySelector('[data-dyn]')) return;

  // Defensive no-op if delivery-config.js hasn't loaded
  if (typeof window.SMOKEYS_DELIVERY === 'undefined') {
    if (typeof console !== 'undefined' && console.error) {
      console.error('[DELIVERY-UI] SMOKEYS_DELIVERY not found — delivery-config.js must load first. Aborting.');
    }
    return;
  }

  var SD = window.SMOKEYS_DELIVERY;

  // --- Zone quote cache (hub-aware) ---
  var zoneQuoteCache = {};

  function getHubIdForMarket(marketId) {
    var mktId = marketId || SD.getCurrentMarketId() || 'edmonton';
    var market = SD.getMarketConfig(mktId);
    if (market && market.hub && market.hub.id) return String(market.hub.id);
    var cur = (typeof window.getCurrentStore === 'function') ? window.getCurrentStore() : null;
    if (cur && cur.store && cur.store.id) return String(cur.store.id);
    return mktId;
  }

  function getCachedZoneQuote(zoneId, marketId) {
    var hubId = getHubIdForMarket(marketId);
    var key = hubId + ':' + marketId + ':' + zoneId;
    if (zoneQuoteCache[key]) return zoneQuoteCache[key];
    var quote = SD.getZoneQuote(zoneId, marketId);
    if (quote) zoneQuoteCache[key] = quote;
    return quote;
  }

  function clearCache() { zoneQuoteCache = {}; }

  // --- Format helper ---
  function fmt(amount) { return SD.formatMoney(amount); }

  // --- Main update function ---
  function applyQuote(quote) {
    var isPickup = quote && quote.pickupOnly === true;
    var marketId = (quote && quote.marketId) || SD.getCurrentMarketId() || 'edmonton';

    // Toggle delivery-only / pickup-only wrappers
    var deliveryOnlyEls = document.querySelectorAll('[data-delivery-only]');
    var pickupOnlyEls = document.querySelectorAll('[data-pickup-only]');
    for (var d = 0; d < deliveryOnlyEls.length; d++) {
      deliveryOnlyEls[d].style.display = isPickup ? 'none' : '';
    }
    for (var p = 0; p < pickupOnlyEls.length; p++) {
      pickupOnlyEls[p].style.display = isPickup ? '' : 'none';
    }

    // Scan all [data-dyn] elements
    var elements = document.querySelectorAll('[data-dyn]');
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      var dynType = el.getAttribute('data-dyn');
      var zone = el.getAttribute('data-zone');
      var elMarket = el.getAttribute('data-market') || marketId;
      var zoneLock = el.hasAttribute('data-zone-lock');

      // Get the right data source
      var src = null;
      if (zone && zoneLock) {
        src = getCachedZoneQuote(zone, elMarket);
      } else if (quote && !quote.pickupOnly && quote.inRange !== false) {
        src = quote;
      } else if (zone) {
        // Fallback: zone quote even without zone-lock
        src = getCachedZoneQuote(zone, elMarket);
      }

      if (isPickup && dynType !== 'pickupEta') {
        el.classList.add('dyn-pickup-only');
        continue;
      }

      if (!src) continue;

      // Apply value by type
      switch (dynType) {
        case 'expressFee': {
          var isLocked = el && el.hasAttribute && el.hasAttribute('data-zone-lock');

          // Zone-locked pages: prefer the precomputed display range
          if (isLocked && src.expressFeeRange) {
            el.textContent = src.expressFeeRange;

          // Default behavior (live quote): use the formatted numeric fee
          } else if (typeof src.expressFee === 'number') {
            el.textContent = src.expressFeeFormatted;

          // Fallback
          } else if (src.expressFeeRange) {
            el.textContent = src.expressFeeRange;
          }
          break;
        }

        case 'standardFee': {
          var isLocked = el && el.hasAttribute && el.hasAttribute('data-zone-lock');

          // Zone-locked pages: prefer the precomputed display range
          if (isLocked && src.standardFeeRange) {
            el.textContent = src.standardFeeRange;

          // Default behavior (live quote): use the formatted numeric fee
          } else if (typeof src.standardFee === 'number') {
            el.textContent = src.standardFeeFormatted;

          // Fallback
          } else if (src.standardFeeRange) {
            el.textContent = src.standardFeeRange;
          }
          break;
        }

        case 'minOrder':
          if (typeof src.minOrder === 'number') {
            el.textContent = src.minOrderFormatted;
          }
          break;

        case 'freeThreshold':
          if (src.freeThresholdFormatted) {
            el.textContent = src.freeThresholdFormatted;
          } else if (typeof src.freeThreshold === 'number') {
            el.textContent = fmt(src.freeThreshold);
          }
          break;

        case 'expressEta': {
          var isLocked = el && el.hasAttribute && el.hasAttribute('data-zone-lock');
          if (isLocked && src.expressEtaRange) {
            el.textContent = src.expressEtaRange;
          } else if (src.expressEta) {
            el.textContent = src.expressEta;
          } else if (src.expressEtaFormatted) {
            el.textContent = src.expressEtaFormatted;
          } else if (typeof src.etaExpressMin === 'number') {
            el.textContent = src.etaExpressMin + '-' + src.etaExpressMax + ' min';
          }
          break;
        }

        case 'standardEta': {
          var isLocked = el && el.hasAttribute && el.hasAttribute('data-zone-lock');
          if (isLocked && src.standardEtaRange) {
            el.textContent = src.standardEtaRange;
          } else if (src.standardEta) {
            el.textContent = src.standardEta;
          } else if (src.standardEtaFormatted) {
            el.textContent = src.standardEtaFormatted;
          } else if (typeof src.etaStandardMin === 'number') {
            el.textContent = src.etaStandardMin + '-' + src.etaStandardMax + ' min';
          }
          break;
        }

        case 'distance':
          if (typeof src.drivingKm === 'number') {
            el.textContent = src.drivingKm.toFixed(1) + ' km';
          } else if (typeof src.km === 'number') {
            el.textContent = src.km.toFixed(1) + ' km';
          }
          break;

        case 'storeHours':
          if (window.STORE_HOURS) {
            el.textContent = window.STORE_HOURS.openTimeShort + ' – ' + window.STORE_HOURS.closeTimeShort;
          }
          break;

        case 'deliveryHours':
          if (window.STORE_HOURS) {
            el.textContent = '11:00am – ' + window.STORE_HOURS.closeTimeShort;
          }
          break;

        case 'expressCutoff':
          if (window.STORE_HOURS) {
            el.textContent = window.STORE_HOURS.expressDeliveryCutoff;
          }
          break;

        case 'standardCutoff':
          if (window.STORE_HOURS) {
            el.textContent = window.STORE_HOURS.standardDeliveryCutoff;
          }
          break;

        case 'maxRange':
          var maxKm = SD.getMaxDeliveryKm(elMarket);
          el.textContent = maxKm + ' km';
          break;

        case 'pickupEta':
          var defaults = SD.getZoneDefaults(zone, elMarket);
          if (defaults && defaults.pickupEta) {
            el.textContent = defaults.pickupEta;
          }
          break;

        case 'expressAvailable':
          if (src.expressAvailable !== undefined) {
            el.textContent = src.expressAvailable ? 'Available' : 'Not available';
          }
          break;

        case 'minFee':
          // Alias: express fee (min fee shown on zone cards)
          if (typeof src.expressFee === 'number') {
            el.textContent = src.expressFeeFormatted;
          }
          break;
      }

      // Update CSS classes for styling hooks
      if (zoneLock) {
        el.classList.add('dyn-zone-quote');
        el.classList.remove('dyn-live', 'dyn-default');
      } else if (quote && quote.inRange) {
        el.classList.add('dyn-live');
        el.classList.remove('dyn-zone-quote', 'dyn-default');
      }
    }

    // Visibility toggles
    var inRangeEls = document.querySelectorAll('[data-dyn="inRange"]');
    for (var ir = 0; ir < inRangeEls.length; ir++) {
      inRangeEls[ir].style.display = (quote && quote.inRange) ? '' : 'none';
    }
    var oorEls = document.querySelectorAll('[data-dyn="outOfRange"]');
    for (var oor = 0; oor < oorEls.length; oor++) {
      oorEls[oor].style.display = (quote && !quote.inRange && !isPickup) ? '' : 'none';
    }
  }

  // --- Pre-populate zone quotes ---
  function applyZoneQuotes() {
    var zoneEls = document.querySelectorAll('[data-zone-lock][data-zone]');
    var seen = {};
    for (var i = 0; i < zoneEls.length; i++) {
      var z = zoneEls[i].getAttribute('data-zone');
      var m = zoneEls[i].getAttribute('data-market') || SD.getCurrentMarketId() || 'edmonton';
      var key = m + ':' + z;
      if (!seen[key]) {
        seen[key] = true;
        getCachedZoneQuote(z, m);
      }
    }
  }

  // --- Load and apply ---
  function refresh() {
    applyZoneQuotes();
    var quote = SD.getQuote() || SD.getCachedQuote();
    applyQuote(quote);
  }

  // --- Prefetch zone driving km ---
  function prefetchZones() {
    if (typeof SD.prefetchZoneDrivingKm !== 'function') return;
    var zoneEls = document.querySelectorAll('[data-zone-lock][data-zone]');
    var byMarket = {};
    for (var i = 0; i < zoneEls.length; i++) {
      var z = zoneEls[i].getAttribute('data-zone');
      var m = zoneEls[i].getAttribute('data-market') || SD.getCurrentMarketId() || 'edmonton';
      if (!z) continue;
      if (!byMarket[m]) byMarket[m] = [];
      if (byMarket[m].indexOf(z) === -1) byMarket[m].push(z);
    }
    for (var mkt in byMarket) {
      if (Object.prototype.hasOwnProperty.call(byMarket, mkt)) {
        SD.prefetchZoneDrivingKm(mkt, byMarket[mkt]);
      }
    }
  }

  // --- Initialize ---
  function init() {
    prefetchZones();
    refresh();

    // Event listeners
    window.addEventListener('deliveryQuoteChanged', function(e) { applyQuote(e.detail); });
    window.addEventListener('zoneDrivingKmCached', function() { clearCache(); refresh(); });
    window.addEventListener('userPositionChanged', function() { setTimeout(refresh, 100); });
    window.addEventListener('storeSelected', function() { clearCache(); setTimeout(refresh, 50); });
    window.addEventListener('deliveryModeChanged', function() { clearCache(); setTimeout(refresh, 50); });

    // Shopify theme editor hooks: re-scan DOM when sections change
    document.addEventListener('shopify:section:load', function() { clearCache(); refresh(); });
    document.addEventListener('shopify:section:unload', function() { clearCache(); refresh(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for manual updates
  window.SMOKEYS_DELIVERY_UI = {
    refresh: refresh,
    clearCache: clearCache
  };

})();
